import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { BaseChartModel } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { GrowthChartsCacheData } from "@/Patient/Models/GrowthCharts/GrowthChartsCacheData";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { GrowthChartsMainContentItem } from "./GrowthChartsMainContentItem";
import { GrowthChartsNavigationBar } from "./GrowthChartsNavigationBar";

interface GrowthChartsMainContentProps {
    cacheData: GrowthChartsCacheData;
    charts: BaseChartModel[];
    filterStartDate: Date;
    filterEndDate: Date;
    unit: UnitSystem;
    editExistingVitalSign: (vitalSign: MedicalItemVitalSign, isFenton: boolean, reason: string) => void;
}

class GrowthChartsMainContentState {
    public activeChart: BaseChartModel;
}

class GrowthChartsMainContent extends BaseReactComponent<GrowthChartsMainContentProps, GrowthChartsMainContentState> {
    private mainContentRef: HTMLElement;

    constructor(props: GrowthChartsMainContentProps) {
        super(props);
        this.state = {
            activeChart: props.charts.firstOrDefault()
        };
    }

    public componentDidUpdate(): void {
        this.handleOnScroll();
    }

    public render(): JSX.Element {
        return (
            <div>
                <GrowthChartsNavigationBar
                    activeChart={this.state.activeChart}
                    charts={this.props.charts}
                    onChartSelected={chart => this.scrollToChart(chart)}
                />
                {this.renderChartView()}
            </div>
        );
    }

    private renderChartView(): JSX.Element {
        return (
            <div
                className="main-content"
                ref={ref => (this.mainContentRef = ref)}
                onScroll={() => this.handleOnScroll()}
            >
                {this.props.charts.map(c => (
                    <GrowthChartsMainContentItem
                        key={c.id}
                        chart={c}
                        cacheData={this.props.cacheData}
                        unitSystem={this.props.unit}
                        editExistingVitalSign={(vitalSign, reason) => this.props.editExistingVitalSign(vitalSign, c.isFentonChart, reason)}
                        isBornPremature={c.patient.IsBornPremature}
                        bornPrematureWeeks={c.patient.BornPrematureWeeks}
                    />
                ))}
            </div>
        );
    }

    private handleOnScroll(): void {
        let children: HTMLElement[] = [];
        this.mainContentRef.childNodes.forEach((node: HTMLElement) => {
            if (this.isScrollPassMiddleOf(node))
                children.push(node);
        });

        const firstNode = children.firstOrDefault();
        if (firstNode.id !== this.state.activeChart.id)
            this.mutateState(s => s.activeChart = this.props.charts.firstOrDefault(c => c.id === firstNode.id));
    }

    private isScrollPassMiddleOf(node: HTMLElement): boolean {
        const scrollTop = (node.offsetTop - this.mainContentRef.offsetTop) + (node.offsetHeight / 2);
        return this.mainContentRef.scrollTop <= scrollTop;
    }

    private scrollToChart(chart: BaseChartModel): void {
        const chartDiv: HTMLDivElement = this.mainContentRef.querySelector(`#${chart.id}`);
        this.mainContentRef.scrollTo({
            top: chartDiv.offsetTop - this.mainContentRef.offsetTop,
            behavior: "smooth"
        });
    }
}

export { GrowthChartsMainContent };
